'use client';

import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';

const navItems = [
  { href: '/microthemes-pyq', label: 'PYQ Microthemes' },
  { href: '/syllabus', label: 'UPSC Syllabus' },
  { href: '/mains-test-series-comparison', label: 'Mains Test Series' }
];

const blockColors = [
  {
    article: 'border-sky-200 bg-sky-50/60',
    eyebrow: 'text-sky-700',
    line: 'border-sky-400',
    note: 'bg-sky-100 text-sky-900'
  },
  {
    article: 'border-emerald-200 bg-emerald-50/60',
    eyebrow: 'text-emerald-700',
    line: 'border-emerald-400',
    note: 'bg-emerald-100 text-emerald-900'
  },
  {
    article: 'border-amber-200 bg-amber-50/60',
    eyebrow: 'text-amber-700',
    line: 'border-amber-400',
    note: 'bg-amber-100 text-amber-900'
  },
  {
    article: 'border-indigo-200 bg-indigo-50/60',
    eyebrow: 'text-indigo-700',
    line: 'border-indigo-400',
    note: 'bg-indigo-100 text-indigo-900'
  }
];

const prelims = [
  {
    paper: 'Paper I - General Studies',
    items: [
      'Current events of national and international importance.',
      'History of India and Indian National Movement.',
      'Indian and World Geography - Physical, Social, Economic Geography of India and the World.',
      'Indian Polity and Governance - Constitution, Political System, Panchayati Raj, Public Policy, Rights Issues, etc.',
      'Economic and Social Development - Sustainable Development, Poverty, Inclusion, Demographics, Social Sector initiatives, etc.',
      'General issues on Environmental Ecology, Bio-diversity and Climate Change - that do not require subject specialisation.',
      'General Science.'
    ]
  },
  {
    paper: 'Paper II - Civil Services Aptitude Test',
    note: 'Qualifying paper; minimum qualifying marks are 33%.',
    items: [
      'Comprehension.',
      'Interpersonal skills including communication skills.',
      'Logical reasoning and analytical ability.',
      'Decision-making and problem-solving.',
      'General mental ability.',
      'Basic numeracy: numbers and their relations, orders of magnitude, etc. Class X level.',
      'Data interpretation: charts, graphs, tables, data sufficiency, etc. Class X level.'
    ]
  }
];

const mains = [
  {
    paper: 'Qualifying Paper A - Indian Language',
    items: [
      'Comprehension of given passages.',
      'Precis writing.',
      'Usage and vocabulary.',
      'Short essays.',
      'Translation from English to the Indian language and from the Indian language to English.'
    ]
  },
  {
    paper: 'Qualifying Paper B - English',
    items: [
      'Comprehension of given passages.',
      'Precis writing.',
      'Usage and vocabulary.',
      'Short essays.'
    ]
  },
  {
    paper: 'Paper I - Essay',
    items: [
      'Candidates may be required to write essays on multiple topics.',
      'They will be expected to keep closely to the subject of the essay, arrange ideas in orderly fashion, and write concisely.'
    ]
  },
  {
    paper: 'Paper II - General Studies I',
    subtitle: 'Indian Heritage and Culture, History and Geography of the World and Society',
    items: [
      'Indian culture will cover the salient aspects of art forms, literature and architecture from ancient to modern times.',
      'Modern Indian history from about the middle of the eighteenth century until the present: significant events, personalities, issues.',
      'The Freedom Struggle: its various stages and important contributors/contributions from different parts of the country.',
      'Post-independence consolidation and reorganisation within the country.',
      'History of the world will include events from 18th century such as industrial revolution, world wars, redrawal of national boundaries, colonisation, decolonisation, political philosophies like communism, capitalism, socialism etc., their forms and effect on society.',
      'Salient features of Indian Society, Diversity of India.',
      'Role of women and women organisation, population and associated issues, poverty and developmental issues, urbanisation, their problems and their remedies.',
      'Effects of globalization on Indian society.',
      'Social empowerment, communalism, regionalism and secularism.',
      'Salient features of world physical geography.',
      'Distribution of key natural resources across the world including South Asia and the Indian sub-continent; factors responsible for the location of primary, secondary and tertiary sector industries in various parts of the world including India.',
      'Important geophysical phenomena such as earthquakes, tsunami, volcanic activity, cyclone etc.; geographical features and their location; changes in critical geographical features including water-bodies and ice-caps and in flora and fauna and the effects of such changes.'
    ]
  },
  {
    paper: 'Paper III - General Studies II',
    subtitle: 'Governance, Constitution, Polity, Social Justice and International Relations',
    items: [
      'Indian Constitution: historical underpinnings, evolution, features, amendments, significant provisions and basic structure.',
      'Functions and responsibilities of the Union and the States, issues and challenges pertaining to the federal structure, devolution of powers and finances up to local levels and challenges therein.',
      'Separation of powers between various organs, dispute redressal mechanisms and institutions.',
      'Comparison of the Indian constitutional scheme with that of other countries.',
      'Parliament and State legislatures: structure, functioning, conduct of business, powers and privileges and issues arising out of these.',
      'Structure, organisation and functioning of the Executive and the Judiciary; ministries and departments of the Government; pressure groups and formal/informal associations and their role in the polity.',
      'Salient features of the Representation of People Act.',
      'Appointment to various Constitutional posts, powers, functions and responsibilities of various Constitutional Bodies.',
      'Statutory, regulatory and various quasi-judicial bodies.',
      'Government policies and interventions for development in various sectors and issues arising out of their design and implementation.',
      'Development processes and the development industry: the role of NGOs, SHGs, various groups and associations, donors, charities, institutional and other stakeholders.',
      'Welfare schemes for vulnerable sections of the population by the Centre and States and the performance of these schemes; mechanisms, laws, institutions and bodies constituted for the protection and betterment of these vulnerable sections.',
      'Issues relating to development and management of Social Sector/Services relating to Health, Education, Human Resources.',
      'Issues relating to poverty and hunger.',
      'Important aspects of governance, transparency and accountability, e-governance applications, models, successes, limitations and potential; citizens charters, transparency and accountability and institutional and other measures.',
      'Role of civil services in a democracy.',
      'India and its neighbourhood relations.',
      'Bilateral, regional and global groupings and agreements involving India and/or affecting India interests.',
      'Effect of policies and politics of developed and developing countries on India interests, Indian diaspora.',
      'Important International institutions, agencies and fora, their structure and mandate.'
    ]
  },
  {
    paper: 'Paper IV - General Studies III',
    subtitle: 'Technology, Economic Development, Bio-diversity, Environment, Security and Disaster Management',
    items: [
      'Indian Economy and issues relating to planning, mobilisation of resources, growth, development and employment.',
      'Inclusive growth and issues arising from it.',
      'Government Budgeting.',
      'Major crops, cropping patterns in various parts of the country, different types of irrigation and irrigation systems, storage, transport and marketing of agricultural produce and issues and related constraints; e-technology in the aid of farmers.',
      'Issues related to direct and indirect farm subsidies and minimum support prices; Public Distribution System, objectives, functioning, limitations, revamping; issues of buffer stocks and food security; technology missions; economics of animal-rearing.',
      'Food processing and related industries in India: scope and significance, location, upstream and downstream requirements, supply chain management.',
      'Land reforms in India.',
      'Effects of liberalisation on the economy, changes in industrial policy and their effects on industrial growth.',
      'Infrastructure: energy, ports, roads, airports, railways etc.',
      'Investment models.',
      'Science and Technology: developments and their applications and effects in everyday life.',
      'Achievements of Indians in science and technology; indigenisation of technology and developing new technology.',
      'Awareness in the fields of IT, Space, Computers, robotics, nano-technology, bio-technology and issues relating to intellectual property rights.',
      'Conservation, environmental pollution and degradation, environmental impact assessment.',
      'Disaster and disaster management.',
      'Linkages between development and spread of extremism.',
      'Role of external state and non-state actors in creating challenges to internal security.',
      'Challenges to internal security through communication networks, role of media and social networking sites in internal security challenges, basics of cyber security; money-laundering and its prevention.',
      'Security challenges and their management in border areas; linkages of organised crime with terrorism.',
      'Various Security forces and agencies and their mandate.'
    ]
  },
  {
    paper: 'Paper V - General Studies IV',
    subtitle: 'Ethics, Integrity and Aptitude',
    items: [
      'Ethics and Human Interface: essence, determinants and consequences of ethics in human actions; dimensions of ethics; ethics in private and public relationships. Human values: lessons from the lives and teachings of great leaders, reformers and administrators; role of family, society and educational institutions in inculcating values.',
      'Attitude: content, structure, function; its influence and relation with thought and behaviour; moral and political attitudes; social influence and persuasion.',
      'Aptitude and foundational values for Civil Service: integrity, impartiality and non-partisanship, objectivity, dedication to public service, empathy, tolerance and compassion towards the weaker sections.',
      'Emotional intelligence: concepts, and their utilities and application in administration and governance.',
      'Contributions of moral thinkers and philosophers from India and world.',
      'Public/Civil service values and Ethics in Public administration: status and problems; ethical concerns and dilemmas in government and private institutions; laws, rules, regulations and conscience as sources of ethical guidance; accountability and ethical governance; strengthening of ethical and moral values in governance; ethical issues in international relations and funding; corporate governance.',
      'Probity in Governance: concept of public service; philosophical basis of governance and probity; information sharing and transparency in government, Right to Information, codes of ethics, codes of conduct, citizen charters, work culture, quality of service delivery, utilisation of public funds, challenges of corruption.',
      'Case studies on above issues.'
    ]
  },
  {
    paper: 'Papers VI and VII - Optional Subject',
    items: [
      'Two papers from the optional subject selected by the candidate.',
      'The syllabus for optional subject papers is given separately by UPSC for each optional subject.'
    ]
  }
];

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Link href="/" className="flex items-center gap-3">
          <Image src="/logo.jpg" alt="UPSC IAS Syllabus Logo" width={36} height={36} className="h-9 w-9" priority />
          <span className="text-lg font-semibold text-slate-950">UPSC IAS Syllabus</span>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          {navItems.map((item) => (
            <Link key={item.href} href={item.href} className={`text-sm font-medium ${item.href === '/syllabus' ? 'text-sky-700' : 'text-slate-700 hover:text-sky-700'}`}>
              {item.label}
            </Link>
          ))}
        </div>

        <button className="md:hidden" onClick={() => setMobileMenuOpen((open) => !open)} aria-label="Toggle navigation">
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {mobileMenuOpen && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
          {navItems.map((item) => (
            <Link key={item.href} href={item.href} className="block rounded px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}

function SyllabusBlock({ section, color = blockColors[0] }) {
  return (
    <article className={`rounded-lg border p-6 ${color.article}`}>
      <h3 className="text-xl font-bold text-slate-950">{section.paper}</h3>
      {section.subtitle && <p className={`mt-1 text-sm font-semibold ${color.eyebrow}`}>{section.subtitle}</p>}
      {section.note && <p className={`mt-3 rounded-md p-3 text-sm ${color.note}`}>{section.note}</p>}
      <ul className="mt-5 space-y-3">
        {section.items.map((item) => (
          <li key={item} className={`border-l-2 pl-3 text-sm leading-6 text-slate-700 ${color.line}`}>
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function SyllabusPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Header />

      <main>
        <section className="border-b border-slate-200 bg-gradient-to-br from-white via-sky-50 to-indigo-50 px-4 py-12 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-sky-700">UPSC CSE syllabus</p>
            <h1 className="text-4xl font-bold tracking-tight text-slate-950 md:text-5xl">Exact UPSC Prelims and Mains syllabus</h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-700">
              The page keeps only the official syllabus structure for Civil Services Preliminary and Main Examination. Optional-subject syllabi are not expanded here because UPSC publishes each optional separately.
            </p>
          </div>
        </section>

        <section className="px-4 py-12 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-6 text-2xl font-bold text-slate-950">Preliminary Examination</h2>
            <div className="grid gap-5 lg:grid-cols-2">
              {prelims.map((section, index) => (
                <SyllabusBlock key={section.paper} section={section} color={blockColors[index % blockColors.length]} />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 pb-14 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <h2 className="mb-6 text-2xl font-bold text-slate-950">Main Examination</h2>
            <div className="space-y-5">
              {mains.map((section, index) => (
                <SyllabusBlock key={section.paper} section={section} color={blockColors[(index + 2) % blockColors.length]} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-950 text-gray-400 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Image
                  src="/logo-bg-removed.png"
                  alt="UPSC IAS Syllabus Logo"
                  width={42}
                  height={42}
                  className="w-18 h-18"
                />
                <span className="text-white font-bold">UPSC IAS Syllabus</span>
              </div>
              <p className="text-sm">UPSC syllabus, microthemes and previous year questions.</p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <Link href="/microthemes-pyq" className="block hover:text-white transition">PYQ Microthemes</Link>
                <Link href="/syllabus" className="block hover:text-white transition">Syllabus</Link>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Mains</h4>
              <div className="space-y-2 text-sm">
                <Link href="/mains-test-series-comparison" className="block hover:text-white transition">Mains Test Series</Link>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; 2025 UPSC IAS Syllabus. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
